import { batchApi } from '@ardoq/api';
import { isArdoqError, isTruthy } from '@ardoq/common-helpers';
import { logError } from '@ardoq/logging';
import { confirm, ConfirmConfig, ModalSize } from '@ardoq/modal';
import { type I18nLocale, t } from '@ardoq/i18n';
import { Column, GridApi, RowSelectionOptions } from 'ag-grid-enterprise';

import {
  InventoryColumnPinned,
  InventoryColumnState,
  SortOrder,
  ArdoqId,
  ProvenanceSource,
  type QueryBuilderSubquery,
} from '@ardoq/api-types';
import { debounce, isEmpty, omit, range } from 'lodash';
import { createComponentsOperations } from './createComponents/createComponentsOperations';
import {
  openCreateComponentsDrawerWithParsedComponentValues,
  insertNewComponentRow,
  insertNewComponentRowWithEmptyWorkspace,
  getWorkspaceIdModelIdAndComponentTypeIdFromDataSource,
  getEditableWorkspaceIds,
} from './createComponents/insertNewRowsInAgGrid';
import { mapFilterModelToSubquery } from './filters/mapFilterModelToSubquery';
import { mapSubqueryToFilterModel } from './filters/mapSubqueryToFilterModel';
import { ExtendedRowNode, InventoryContext, InventoryGridApi } from './types';
import { getDisplayedColumnsFromGridApi } from './mapAgGridParamsToArdoqFormat';
import { SetFilterEntry } from './filters/types';
import { inventoryGridAPIOperations } from './inventoryGridAPIOperations';
import { hierarchyInAgGridOperations } from './hierarchy/hierarchyInAgGridOperations';
import { AG_GRID_SELECTION_COLUMN_ID } from './columns/selectAndDeleteColumnDefinition';
import { columnOperations } from './columns/columnOperations';
import { refreshServerSideForGrid } from './serverSideRefresh';
import { bulkEditFieldOperations } from './updateComponents/bulkEditField/bulkEditFieldOperations';

const SEARCH_FILTER_DEBOUNCE_MS = 300;

export const getInventoryGridApi = (gridApi: GridApi): InventoryGridApi => {
  const debouncedOnFilterChanged = debounce(
    () => gridApi.onFilterChanged(),
    SEARCH_FILTER_DEBOUNCE_MS
  );

  return {
    getAllColumns: () => {
      return gridApi
        .getAllGridColumns()
        .filter(col => col.getColId() !== AG_GRID_SELECTION_COLUMN_ID)
        .map(it => agGridColumnToInventoryColumnState(it));
    },
    getBulkEditableColumns: () => {
      const context = gridApi.getGridOption('context') as
        | InventoryContext
        | undefined;
      return context
        ? bulkEditFieldOperations.getBulkEditableColumns(
            gridApi,
            context,
            context.config.locale
          )
        : [];
    },
    getDisplayedColumns: () => getDisplayedColumnsFromGridApi(gridApi),
    getSorting: () => {
      const sortedColumn = gridApi
        .getAllGridColumns()
        .find(column => Boolean(column.getSort()));
      const sortDirection = sortedColumn?.getSort();
      if (!sortedColumn || !sortDirection) {
        return null;
      }
      return columnOperations.toSorting(
        columnOperations.toLeafColumnDefs(gridApi.getColumnDefs() ?? []),
        sortedColumn.getColId(),
        sortDirection
      );
    },
    getFiltersAsSubquery: () => {
      return mapFilterModelToSubquery(
        gridApi.getFilterModel(),
        columnOperations.toLeafColumnDefs(gridApi.getColumnDefs() ?? [])
      );
    },
    getDisplayedRowCount: () => gridApi.getDisplayedRowCount(),
    enterCreateComponentsMode: async ({ shouldAskToPasteValues }) => {
      selectComponentTypeNameAndWorkspaceColumns(gridApi);
      deselectAllRowsAndDisableComponentSelection(gridApi);
      await pasteRowsFromClipboardOrAddNewRow(gridApi, shouldAskToPasteValues);
    },
    exitCreateComponentMode: async () => {
      const savingSucceededOrNoNeed =
        await saveUnpersistedComponentsWithNameFilledIn(gridApi);
      if (savingSucceededOrNoNeed) {
        gridApi.setGridOption('pinnedTopRowData', []);
        enableComponentSelection(gridApi);
        refreshRowsWithDelayAndOverlay(gridApi);
      }
      return savingSucceededOrNoNeed;
    },
    openColumnSelectionPanel: () => {
      gridApi.showColumnChooser();
    },
    closeColumnSelectionPanel: () => {
      gridApi.hideColumnChooser();
    },
    refreshServerSide: (params, options) =>
      refreshServerSideForGrid(gridApi, params, options),
    deselectAllRows: gridApi.deselectAll,
    selectFirstRows: count => {
      const nodes = range(Math.min(count, gridApi.getDisplayedRowCount()))
        .map(index => gridApi.getDisplayedRowAtIndex(index))
        .filter(isTruthy);
      gridApi.setNodesSelected({ nodes, newValue: true });
    },
    addNewRow: () => {
      addNewRow(gridApi);
    },
    setSearchByNameFilter: searchPhrase => {
      const searchByNameFilter =
        searchPhrase.trim().length > 0
          ? {
              filterType: 'text',
              type: 'contains',
              filter: searchPhrase,
            }
          : null; // We disable the name filter when searchPhrase is an empty string.

      gridApi.setColumnFilterModel('name', searchByNameFilter);
      debouncedOnFilterChanged();
    },
    getNameFilterValue: () => {
      const filterModel = gridApi.getFilterModel();
      return filterModel.name?.filter ?? '';
    },
    clearAllFilters: (fixedFilterColumnKeys?: ReadonlyArray<string>) => {
      debouncedOnFilterChanged.cancel();
      if (!fixedFilterColumnKeys?.length) {
        gridApi.setFilterModel(null);
        return;
      }
      const currentFilterModel = gridApi.getFilterModel();
      const lockedFilters = Object.fromEntries(
        Object.entries(currentFilterModel).filter(([key]) =>
          fixedFilterColumnKeys.includes(key)
        )
      );
      gridApi.setFilterModel(isEmpty(lockedFilters) ? null : lockedFilters);
    },
    isAnyFilterPresent: (fixedFilterColumnKeys?: ReadonlyArray<string>) =>
      Object.keys(gridApi.getFilterModel()).some(
        key => !fixedFilterColumnKeys?.includes(key)
      ),
    getScopeData: () => gridApi.getGridOption('context')?.scopeData,
    getSelectedRows: gridApi.getSelectedRows,
    isHierarchyEnabled: () => {
      const context = gridApi.getGridOption('context') as
        | InventoryContext
        | undefined;
      return context?.hierarchy.isHierarchyEnabled ?? false;
    },
    getSingleComponentTypeName: () =>
      dataSourceSingleComponentTypeName(gridApi) ??
      filterSingleComponentTypeName(gridApi) ??
      workspaceSingleComponentTypeName(gridApi),
    hasSingleComponentTypeGuarantee: () =>
      dataSourceSingleComponentTypeName(gridApi) !== null ||
      filterSingleComponentTypeName(gridApi) !== null ||
      workspaceSingleComponentTypeName(gridApi) !== null,
    removePinnedRows: (componentIds: ArdoqId[]) =>
      inventoryGridAPIOperations.removeComponentsFromPinnedRows(
        gridApi,
        componentIds
      ),
    expandRowNode: (componentId: ArdoqId) => {
      const rowNode = gridApi.getRowNode(componentId);
      if (rowNode) {
        gridApi.setRowNodeExpanded(rowNode, true);
      }
    },
    markNodeForExpansion: (componentId: ArdoqId) => {
      const context = gridApi.getGridOption('context') as InventoryContext;
      if (context) {
        hierarchyInAgGridOperations.markNodeForReexpansion(
          context.hierarchy,
          componentId
        );
      }
    },
    captureExpandedNodesForReexpansion: () =>
      hierarchyInAgGridOperations.captureExpandedNodesForReexpansion(gridApi),
    showLoadingOverlay: () => gridApi.setGridOption('loading', true),
    hideLoadingOverlay: () => gridApi.setGridOption('loading', false),

    isGroupNodeInGrid: componentId => {
      const node = gridApi.getRowNode(componentId);
      return (node?.allChildrenCount ?? 0) > 0;
    },

    getRowChildCount: componentId => {
      const node = gridApi.getRowNode(componentId);
      return node?.allChildrenCount ?? 0;
    },

    refreshRowCellsWithCount: (componentId, count) => {
      const rowNode = gridApi.getRowNode(componentId);
      if (!rowNode?.data) return;

      // allChildrenCount is only set by AG Grid when the node is first created.
      // We must update it manually before repainting so the cell shows the correct count.
      rowNode.allChildrenCount = count;
      gridApi.refreshCells({ rowNodes: [rowNode], force: true });
    },

    applyComponentUpdatesToRows: components =>
      inventoryGridAPIOperations.applyComponentUpdatesToRows(
        gridApi,
        components
      ),

    showColumnFilter: (columnKey: string) => {
      gridApi.showColumnFilter(columnKey);
    },
    closeColumnFilter: () => {
      gridApi.hideColumnFilter();
    },
    closeContextMenu: () => {
      gridApi.hidePopupMenu();
    },
    clearColumnFilter: (columnKey: string) => {
      columnOperations
        .getColumnIdsBackedByField(
          columnOperations.toLeafColumnDefs(gridApi.getColumnDefs() ?? []),
          columnKey
        )
        .forEach(colId => gridApi.setColumnFilterModel(colId, null));
      debouncedOnFilterChanged();
    },
    applyFilterSubquery: async (subquery: QueryBuilderSubquery) => {
      await Promise.all(
        Object.entries(mapSubqueryToFilterModel(subquery)).map(
          ([colId, filterEntry]) =>
            gridApi.setColumnFilterModel(colId, filterEntry)
        )
      );
      debouncedOnFilterChanged();
      debouncedOnFilterChanged.flush();
    },
  };
};

const addNewRow = (gridApi: GridApi) => {
  const pinnedTopRowData = gridApi.getGridOption('pinnedTopRowData');
  const latestComponentRowData = pinnedTopRowData?.find(
    row =>
      !createComponentsOperations.isCreateComponentHelperTextRow(row._id) &&
      row.rootWorkspace &&
      row.typeId &&
      row.model
  );

  if (latestComponentRowData) {
    insertNewComponentRow(gridApi, {
      workspaceId: latestComponentRowData.rootWorkspace,
      componentTypeId: latestComponentRowData.typeId,
      modelId: latestComponentRowData.model,
    });
    return;
  }

  if (getEditableWorkspaceIds(gridApi).length > 1) {
    insertNewComponentRowWithEmptyWorkspace(gridApi);
    return;
  }

  const workspaceIdModelIdAndComponentTypeId =
    getWorkspaceIdModelIdAndComponentTypeIdFromDataSource(gridApi);
  if (workspaceIdModelIdAndComponentTypeId) {
    insertNewComponentRow(gridApi, workspaceIdModelIdAndComponentTypeId);
  }
};

const selectComponentTypeNameAndWorkspaceColumns = (gridApi: GridApi) => {
  gridApi.setColumnsVisible(['type', 'rootWorkspace', 'name'], true);
  gridApi.autoSizeColumns(['type', 'rootWorkspace']);
  gridApi.moveColumns(['type', 'rootWorkspace', 'name'], 1); // we don't move the "select" column
};

const deselectAllRowsAndDisableComponentSelection = (gridApi: GridApi) => {
  gridApi.deselectAll();
  gridApi.setGridOption('rowSelection', {
    ...((gridApi.getGridOption('rowSelection') as RowSelectionOptions) ?? {}),
    isRowSelectable: () => false,
  });
};

const enableComponentSelection = (gridApi: GridApi) => {
  gridApi.setGridOption('rowSelection', {
    ...((gridApi.getGridOption('rowSelection') as RowSelectionOptions) ?? {}),
    isRowSelectable: () => true,
  });
  gridApi.refreshCells({ force: true, columns: [AG_GRID_SELECTION_COLUMN_ID] });
};

const mapPinnedValue = (column: Column): InventoryColumnPinned => {
  const pinned = column.getPinned();
  if (pinned === 'left') {
    return 'left';
  }
  if (pinned === 'right') {
    return 'right';
  }
  return null;
};

const agGridColumnToInventoryColumnState = (
  column: Column
): InventoryColumnState => {
  return {
    key: column.getColId(),
    pinned: mapPinnedValue(column),
    isVisible: column.isVisible(),
    sort: column.getSort()
      ? column.getSort() === 'asc'
        ? SortOrder.ASC
        : SortOrder.DESC
      : null,
  };
};

/**
 * When user click "Finish editing" and exits "create components" mode before they
 * move focus to another cell, we want to wait a moment to include the new row in the
 * refreshed data.
 *
 * In hierarchy mode we must clear the hierarchy cache so that the next fetch starts
 * from page 0. Without this, any newly created component that matches the active filter
 * is missing from the cache. The server count rises by 1 but the pagination logic tries
 * to fetch a non-existent next page, causing repeated root-count and search requests
 * until the loop detector fires (≈3 s loading state).
 */
const refreshRowsWithDelayAndOverlay = (gridApi: GridApi) => {
  gridApi.setGridOption('loading', true);
  setTimeout(() => {
    refreshServerSideForGrid(gridApi);
    gridApi.setGridOption('loading', false);
  }, 500);
};

const pasteRowsFromClipboardOrAddNewRow = async (
  gridApi: GridApi,
  shouldAskToPasteValues: boolean
) => {
  gridApi.hideOverlay(); // hides the "no rows" overlay if no components in the table
  const context = gridApi.getGridOption('context') as InventoryContext;
  const isAlphaEnabled = context.config.isAlphaEnabled;

  const workspaceIdModelIdAndComponentTypeId =
    getWorkspaceIdModelIdAndComponentTypeIdFromDataSource(gridApi);
  if (!workspaceIdModelIdAndComponentTypeId) {
    return;
  }

  if (shouldAskToPasteValues && isAlphaEnabled) {
    const clipboardText = await getTextFromClipboard();
    const count = getLineCountFromText(clipboardText);
    if (count > 1) {
      const confirmed = await confirm(
        dialogConfigForPastingFromClipboard(count, context.i18nLocale)
      );
      if (confirmed) {
        openCreateComponentsDrawerWithParsedComponentValues(
          gridApi,
          workspaceIdModelIdAndComponentTypeId,
          clipboardText
        );
        return;
      }
    }
  }

  if (getEditableWorkspaceIds(gridApi).length > 1) {
    insertNewComponentRowWithEmptyWorkspace(gridApi);
    return;
  }

  insertNewComponentRow(gridApi, workspaceIdModelIdAndComponentTypeId);
};

const getTextFromClipboard = async () => {
  try {
    return await navigator.clipboard.readText();
  } catch (error) {
    return '';
  }
};

const getLineCountFromText = (text: string) => {
  return text.split('\n').length;
};

const dialogConfigForPastingFromClipboard = (
  linesCount: number,
  i18nLocale: I18nLocale
): ConfirmConfig => {
  return {
    modalSize: ModalSize.S,
    title: `[${t(i18nLocale, 'inventory:datasourceInformation.alphaBadge', 'Alpha')}] ${t(
      i18nLocale,
      'inventory:pasteNewComponents.title',
      'Paste new components'
    )}`,
    text: t(
      i18nLocale,
      'inventory:pasteNewComponents.text',
      { count: linesCount },
      `It looks like you have multiple lines of text in your clipboard. Do you want to create ${linesCount} new components from that content?`
    ),
    confirmButtonTitle: t(
      i18nLocale,
      'inventory:pasteNewComponents.confirmButtonTitle',
      'Paste'
    ),
    cancelButtonTitle: t(
      i18nLocale,
      'inventory:confirmDialogs.cancel',
      'Cancel'
    ),
  };
};

const dialogConfigForFailedSavingOfNewComponents = (
  i18nLocale: I18nLocale
): ConfirmConfig => {
  return {
    title: t(i18nLocale, 'inventory:saveNewComponentsFailed.title', 'Error'),
    text: t(
      i18nLocale,
      'inventory:saveNewComponentsFailed.text',
      'We could not save new components. Please try again or contact support.'
    ),
    confirmButtonTitle: t(i18nLocale, 'inventory:dialogs.ok', 'OK'),
    cancelButtonTitle: '',
  };
};

const saveUnpersistedComponentsWithNameFilledIn = async (
  gridApi: GridApi
): Promise<boolean> => {
  const unpersistedComponents =
    getUnpersistedValidComponentsWithNameFilledIn(gridApi);
  if (unpersistedComponents.length > 0) {
    const batchResponse = await batchApi.execute(
      {
        components: {
          create: unpersistedComponents,
        },
      },
      ProvenanceSource.DATA_INVENTORY
    );
    if (isArdoqError(batchResponse)) {
      logError(batchResponse.error, 'Error batch creating components');
      const inventoryContext = gridApi.getGridOption(
        'context'
      ) as InventoryContext;
      await confirm(
        dialogConfigForFailedSavingOfNewComponents(inventoryContext.i18nLocale)
      );
      return false;
    }
  }
  return true;
};

const getUnpersistedValidComponentsWithNameFilledIn = (gridApi: GridApi) => {
  const topRows = [];
  const pinnedTopRowCount = gridApi.getPinnedTopRowCount();
  for (let i = 0; i < pinnedTopRowCount; i++) {
    const rowNode = gridApi.getPinnedTopRow(i);
    if (rowNode && rowNode.data) {
      topRows.push(rowNode);
    }
  }

  return topRows
    .filter(rowNode => {
      const row = rowNode as ExtendedRowNode;
      return !row._hasError;
    })
    .map(rowNode => rowNode.data)
    .filter(
      component =>
        createComponentsOperations.isComponentNotPersisted(component._id) &&
        (component.name ?? '').trim() !== ''
    )
    .map(component => omit(component, ['_id']));
};

const dataSourceSingleComponentTypeName = (gridApi: GridApi): string | null => {
  const context = gridApi.getGridOption('context') as InventoryContext;
  if (
    context.datasourceSelection.dataSourceType === 'componentType' &&
    context.datasourceSelection.componentTypeNames.length === 1
  ) {
    return context.datasourceSelection.componentTypeNames[0];
  }
  return null;
};

const filterSingleComponentTypeName = (gridApi: GridApi): string | null => {
  const appliedTypeFilter =
    gridApi.getColumnFilterModel<SetFilterEntry>('type');
  if (!appliedTypeFilter || appliedTypeFilter.values.length !== 1) return null;
  const value = appliedTypeFilter.values[0];
  return typeof value === 'string' ? value : null;
};

const workspaceSingleComponentTypeName = (gridApi: GridApi): string | null => {
  const values = gridApi.getColumnDef('type')?.filterParams?.values;
  if (!Array.isArray(values) || values.length !== 1) return null;
  const value = values[0];
  return typeof value === 'string' ? value : null;
};
